import { useState } from 'react';
import { Modal, Typography } from '@sunrun/experience-ui-components';
import { formatDateMMDDYYYY } from 'helpers/formatHelpers';
import { TrainingAlertLevel } from 'hooks/useTrainings';

type StaticModalConfig = {
  type: 'error' | 'warning';
  title: string;
  subtitle: string;
  secondaryButtonText: string;
  primaryButtonText: string;
};

type InfoModalConfig = {
  type: 'info';
  title: string;
  secondaryButtonText: string;
  primaryButtonText: string;
};

const STATIC_MODAL_CONFIG: Record<'red' | 'yellow', StaticModalConfig> = {
  red: {
    type: 'error' as const,
    title: 'Required training overdue',
    subtitle:
      'You have a required training that is past due. Complete it now to maintain access to your sales tools and stay compliant.',
    secondaryButtonText: 'Risk losing access',
    primaryButtonText: 'Complete training',
  },
  yellow: {
    type: 'warning' as const,
    title: 'Required training due soon',
    subtitle:
      'You have a required training due soon. Complete it to maintain access to your sales tools.',
    secondaryButtonText: 'Lose access soon',
    primaryButtonText: 'Complete training',
  },
};

const INFO_MODAL_CONFIG: InfoModalConfig = {
  type: 'info' as const,
  title: 'Required training assigned',
  secondaryButtonText: 'Risk losing access',
  primaryButtonText: 'Complete Training',
};

const TrainingAlertModal = ({
  alertLevel,
  courseUrl,
  dueDate,
}: {
  alertLevel: TrainingAlertLevel;
  courseUrl: string;
  dueDate: string;
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  if (alertLevel === 'info') {
    const { type, title, secondaryButtonText, primaryButtonText } =
      INFO_MODAL_CONFIG;
    const subtitle = `You have a required training due on ${formatDateMMDDYYYY(
      dueDate,
    )}. Complete it now to stay compliant and avoid losing access to sales tools.`;

    return (
      <div onMouseDown={(e) => e.stopPropagation()}>
        <Modal
          testId="training-alert-modal"
          customModalWidth={820}
          type={type}
          title={title}
          children={<Typography size="large">{subtitle}</Typography>}
          primaryButton={{
            text: primaryButtonText,
            onClick: () => {
              window.open(courseUrl, '_blank');
              setDismissed(true);
            },
          }}
          secondaryButton={{
            text: secondaryButtonText,
            onClick: () => setDismissed(true),
          }}
          onClose={() => setDismissed(true)}
        />
      </div>
    );
  }

  const { type, title, subtitle, secondaryButtonText, primaryButtonText } =
    STATIC_MODAL_CONFIG[alertLevel];

  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <Modal
        testId="training-alert-modal"
        customModalWidth={820}
        type={type}
        title={title}
        children={<Typography size="large">{subtitle}</Typography>}
        primaryButton={{
          text: primaryButtonText,
          onClick: () => {
            window.open(courseUrl, '_blank');
            setDismissed(true);
          },
        }}
        secondaryButton={{
          text: secondaryButtonText,
          onClick: () => setDismissed(true),
        }}
        onClose={() => setDismissed(true)}
      />
    </div>
  );
};

export { TrainingAlertModal };
